'use client';
import PropTypes from 'prop-types';
import { deprecatedProp } from '../core/deprecated-prop.js';
import { createComponent } from '../core/create-component.js';
import { SWAN_STYLE_KEY_MAP } from '../head/stylesheets.component.js';
import { useControllableValue } from '../../hooks/use-controllable-value.js';
import { composeEventHandlersUtil } from '../../utilities/compose-event-handlers.util.js';

function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
const ToggleSwitchSize = {
    Standard: 'standard',
    Super: 'super',
    Mini: 'mini'
};
const propTypes = {
    /**
   * @deprecated
   * This is deprecated without a replacement.
   *
   * Size (height) of the toggle.
   * Available options: 'standard', 'super', 'mini'
   * @default standard
   */ size: deprecatedProp(PropTypes.oneOf([
        ToggleSwitchSize.Standard,
        ToggleSwitchSize.Super,
        ToggleSwitchSize.Mini
    ]), 'Sizing is now handled automatically by standardMode/compactMode'),
    /**
   * Whether the on/off text is visually hidden
   */ hideText: PropTypes.bool,
    /**
   * Whether or not the switch is "activated" aka "on"
   */ activated: PropTypes.bool,
    /**
   * Callback fired when the ToggleSwitch wants to change its activated state
   */ onRequestActivatedChange: PropTypes.func,
    /**
   * The initial activated state.
   * Ignored if `activated` is provided
   */ defaultActivated: PropTypes.bool
};
const ToggleSwitch = createComponent({
    displayName: 'ToggleSwitch',
    root: 'button',
    propTypes,
    requiredStyleKeys: SWAN_STYLE_KEY_MAP.toggleSwitch,
    propConfig: {
        derivedProps (props) {
            const { defaultActivated = false, activated: controlledActivated, onRequestActivatedChange: controlledOnRequestActivatedChange } = props;
            const [activated, onRequestActivatedChange] = useControllableValue({
                value: controlledActivated,
                onChange: controlledOnRequestActivatedChange || undefined,
                defaultValue: defaultActivated,
                defaultOnChange: (setActivated, ...onChangeArgs)=>{
                    const [val] = onChangeArgs;
                    setActivated(val);
                }
            });
            return {
                activated: !!activated,
                onRequestActivatedChange
            };
        },
        processProps (props, derivedProps) {
            const { activated, onRequestActivatedChange } = derivedProps;
            return _object_spread_props(_object_spread({}, props), {
                role: 'switch',
                'aria-checked': activated,
                onClick: composeEventHandlersUtil(props.onClick, (event)=>{
                    onRequestActivatedChange(!activated, event);
                })
            });
        },
        classNames (props) {
            const classNameSet = new Set([
                'swan-vanilla-ignore',
                'swan-toggle-switch'
            ]);
            const { hideText, size = ToggleSwitchSize.Standard } = props;
            if (size === ToggleSwitchSize.Mini) classNameSet.add(`swan-toggle-switch-${size}`);
            if (size === ToggleSwitchSize.Super) classNameSet.add(`swan-toggle-switch-${size}`);
            if (hideText) classNameSet.add('swan-toggle-switch-hide-text');
            return classNameSet;
        }
    }
});

export { ToggleSwitch, ToggleSwitchSize };
